import { type AppVersion, type MarketplaceAppByKeyQuery } from '@atlassian/mpac-app-listing';
import {
  AtlassianProductHostingType,
  MarketplaceProgramStatus,
} from '@atlassian/mpac-types/graphql-gateway-types';
import { useLocation } from '@atlassian/mpac-ui';
import { useEffect } from 'react';

import {
  toAnalyticsHosting,
  toAnalyticsPaymentModel,
} from './app/components/Routes/components/AppListingRoutes/components/AppsRoute/utils';
import {
  addVendorGAId,
  contentGroupCompatibleProduct,
  contentGroupHosting,
  contentGroupPaymentModel,
  removeVendorGAId,
  trackPageView as trackGAPageView,
} from './common/utils/analytics/analytics';
import * as webAnalytics from './common/utils/analytics/webAnalytics';

const NOT_A_PARTICIPANT = MarketplaceProgramStatus.NotAParticipant;

const getFormattedCategory = (s: string) => {
  // words should be separated by underscore,
  // no special characters
  return s
    .split(/\W/)
    .filter((token) => token.length !== 0)
    .join('_');
};

const getPageTitleFromAppName = (appName: string) => `${appName} | Atlassian Marketplace`;

type AppListingPageAnalyticsProps = {
  hostingType: AtlassianProductHostingType;
  pricingModel: string;
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  pageRouteSource?: string | null;
};

export const useAppListingPageAnalytics = ({
  hostingType,
  pricingModel,
  appDetails,
  pageRouteSource,
}: AppListingPageAnalyticsProps) => {
  const { pathname } = useLocation();
  const appName = appDetails.name;
  const appId = appDetails.appId;
  const pageTitle = getPageTitleFromAppName(appName);
  const partnerId = appDetails.partner?.id;
  const appKey = appDetails.appKey;

  const cloudFortifiedProgramStatus =
    appDetails.programs?.cloudFortified?.status || NOT_A_PARTICIPANT;
  const bugBountyProgramStatus = {
    bugBountyProgramStatusForCloud:
      appDetails.programs?.bugBountyParticipant?.cloud?.status || NOT_A_PARTICIPANT,
    bugBountyProgramStatusForServer:
      appDetails.programs?.bugBountyParticipant?.server?.status || NOT_A_PARTICIPANT,
    bugBountyProgramStatusForDataCenter:
      appDetails.programs?.bugBountyParticipant?.dataCenter?.status || NOT_A_PARTICIPANT,
  };

  useEffect(() => {
    trackGAPageView(pathname);
    // TODO: Add a referrer here after App Listing page is a part of the SPA flow
    webAnalytics.sendScreenEvent(document.referrer, 'appListingScreen', {
      // Usually, ace analytics would populate page_title and category for us, but
      // since we're firing this lazily as opposed to on page load, we'll have
      // to do this ourselves.
      category: getFormattedCategory(pageTitle),
      poweredBy: 'AMKT_FRONTEND',
      page_title: pageTitle,
      hostingType,
      partnerId,
      appKey,
      appId,
      pricingModel,
      cloudFortifiedProgramStatus,
      ...bugBountyProgramStatus,
      ...(pageRouteSource && { pageRouteSource }),
    });
  }, [appId]);
};

type VendorGAEventProps = {
  appId: string;
  appKey: string;
  appName: string;
  googleAnalyticsId: string | null | undefined;
  buildNumber: string;
};

export const useVendorGoogleAnalytics = ({
  appId,
  appName,
  appKey,
  googleAnalyticsId,
  buildNumber,
}: VendorGAEventProps) => {
  useEffect(() => {
    if (googleAnalyticsId) {
      addVendorGAId(
        googleAnalyticsId,
        appId,
        appKey,
        buildNumber,
        getPageTitleFromAppName(appName)
      );
    }
    return () => {
      removeVendorGAId(googleAnalyticsId, appId);
    };
  }, [googleAnalyticsId, appId]);
};

type GoogleAnalyticsExtraContextProps = {
  cloudAppVersion: AppVersion | null | undefined;
  serverAppVersion: AppVersion | null | undefined;
  dataCenterAppVersion: AppVersion | null | undefined;
};

export const useGoogleAnalyticsExtraContext = ({
  cloudAppVersion,
  serverAppVersion,
  dataCenterAppVersion,
}: GoogleAnalyticsExtraContextProps) => {
  const getContentGroups = () => {
    const hostingOptions: Array<any> = [];
    const paymentModels: Array<any> = [];
    const compatibleProducts: Array<any> = [];

    [cloudAppVersion, serverAppVersion, dataCenterAppVersion].forEach((appVersion) => {
      if (!appVersion?.deployment.compatibleProducts.length) return;

      paymentModels.push(toAnalyticsPaymentModel(appVersion.paymentModel));
      appVersion.deployment.compatibleProducts.forEach((product) => {
        compatibleProducts.push(product.name);
      });
    });

    if (serverAppVersion) {
      hostingOptions.push(toAnalyticsHosting(AtlassianProductHostingType.Server));
    }

    if (cloudAppVersion) {
      hostingOptions.push(toAnalyticsHosting(AtlassianProductHostingType.Cloud));
    }

    if (serverAppVersion) {
      hostingOptions.push(toAnalyticsHosting(AtlassianProductHostingType.DataCenter));
    }

    return { hostingOptions, paymentModels, compatibleProducts };
  };

  useEffect(() => {
    const { hostingOptions, paymentModels, compatibleProducts } = getContentGroups();
    contentGroupHosting(hostingOptions.join(','));
    contentGroupPaymentModel([...new Set(paymentModels)].join(','));
    contentGroupCompatibleProduct([...new Set(compatibleProducts)].join(','));

    return () => {
      contentGroupHosting(null);
      contentGroupPaymentModel(null);
      contentGroupCompatibleProduct(null);
    };
  }, [cloudAppVersion, serverAppVersion, dataCenterAppVersion]);
};
